import React, { useState } from "react";
import { api, errMsg } from "../lib/api";
import { Btn, Input } from "./UI";
import { Link2, X, Copy, RotateCcw, Ban, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const ClientPortalButton = ({ entity }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [portal, setPortal] = useState(entity.client_portal || null);
  const [agencyName, setAgencyName] = useState(entity.client_portal?.agency_name || "");
  const [logoUrl, setLogoUrl] = useState(entity.client_portal?.agency_logo_url || "");
  const [accent, setAccent] = useState(entity.client_portal?.accent_color || "#fbbf24");
  const [weeklyOn, setWeeklyOn] = useState(!!entity.client_portal?.weekly_email?.enabled);
  const [weeklyEmail, setWeeklyEmail] = useState(entity.client_portal?.weekly_email?.recipient || "");
  const initQuotes = () => {
    const cp = entity.client_portal || {};
    if (cp.testimonials?.length) return cp.testimonials.map(t => ({ quote: t.quote || "", author: t.author || "" }));
    if (cp.testimonial?.quote) return [{ quote: cp.testimonial.quote, author: cp.testimonial.author || "" }];
    return [];
  };
  const [quotes, setQuotes] = useState(initQuotes);
  const [showSpend, setShowSpend] = useState(!!entity.client_portal?.show_spend);
  const [inviteMsg, setInviteMsg] = useState("");
  const [templateLoaded, setTemplateLoaded] = useState(false);

  React.useEffect(() => {
    if (!open || templateLoaded) return;
    api.get("/political-os/agency/invite-template").then(({ data }) => {
      if (data.message) setInviteMsg(data.message);
      setTemplateLoaded(true);
    }).catch(() => setTemplateLoaded(true));
  }, [open, templateLoaded]);

  const saveTemplate = async () => {
    try {
      await api.put("/political-os/agency/invite-template", { message: inviteMsg });
      toast.success("Saved as your reusable invite template");
    } catch (e) { toast.error(errMsg(e, "Could not save template")); }
  };

  const act = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/political-os/entities/${entity.id}/client-portal`, {
        action, agency_name: agencyName, agency_logo_url: logoUrl, accent_color: accent,
        weekly_email_enabled: weeklyOn, weekly_email_recipient: weeklyEmail,
        testimonials: quotes.map(q => ({ quote: q.quote.trim(), author: q.author.trim() })).filter(q => q.quote),
        show_spend: showSpend,
        portal_url: portal?.token ? `${window.location.origin}/client/${portal.token}` : null,
      });
      setPortal(data.client_portal);
      toast.success(action === "disable" ? "Portal link disabled" : action === "rotate" ? "New link generated — old one is dead" : "Client portal is live");
    } catch (e) { toast.error(errMsg(e, "Could not update portal")); }
    finally { setBusy(false); }
  };

  const link = portal?.enabled ? `${window.location.origin}/client/${portal.token}` : null;
  const copy = () => { navigator.clipboard?.writeText(link).then(() => toast.success("Link copied")).catch(() => {}); };
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const [quoteReqEmail, setQuoteReqEmail] = useState("");
  const [quoteReqBusy, setQuoteReqBusy] = useState(false);

  const sendQuoteRequest = async (emailIt) => {
    if (emailIt && !quoteReqEmail.trim()) return toast.error("Enter the client's email first");
    setQuoteReqBusy(true);
    try {
      const { data } = await api.post(`/political-os/entities/${entity.id}/client-portal/quote-request`, {
        email: emailIt ? quoteReqEmail.trim() : null, origin_url: window.location.origin,
      });
      if (emailIt) toast.success(data.email_sent ? `Quote request emailed to ${quoteReqEmail.trim()}` : "Link created — email couldn't send, link copied instead");
      if (!emailIt || !data.email_sent) await navigator.clipboard?.writeText(data.link).catch(() => {});
      if (!emailIt) toast.success("Quote link copied — send it to your client");
      setQuoteReqEmail("");
    } catch { toast.error("Could not create quote link"); }
    finally { setQuoteReqBusy(false); }
  };

  const invite = async () => {
    if (!inviteEmail.trim()) return toast.error("Enter the client's email");
    setInviting(true);
    try {
      await api.post(`/political-os/entities/${entity.id}/client-portal/invite`, {
        email: inviteEmail.trim(), portal_url: link, message: inviteMsg,
      });
      toast.success(`Branded dashboard invite sent to ${inviteEmail.trim()}`);
      setInviteEmail("");
    } catch (e) { toast.error(errMsg(e, "Could not send the invite")); }
    finally { setInviting(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`client-portal-btn-${entity.id}`}
        className="text-xs text-slate-400 hover:text-cyan-300 inline-flex items-center gap-1 transition-colors">
        <Link2 className="w-3 h-3" /> Portal
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-app border border-white/10 rounded-lg max-w-md w-full p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="client-portal-modal">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display text-xl font-bold">Client performance portal</h3>
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-slate-400 mb-4">
              Share a read-only, live performance dashboard for <b className="text-slate-200">{entity.name}</b> — branded with YOUR agency logo on top. No login needed, no internal costs shown.
            </p>
            <div className="space-y-3 mb-4">
              <Input label="Agency name (shown in header)" value={agencyName} onChange={(e) => setAgencyName(e.target.value)}
                placeholder="Summit Political Strategies" data-testid="portal-agency-name-input" />
              <Input label="Agency logo URL (optional)" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://youragency.com/logo.png" data-testid="portal-agency-logo-input" />
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Accent color</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} data-testid="portal-accent-input"
                    className="w-10 h-8 rounded border border-white/15 bg-transparent cursor-pointer" />
                  <span className="text-xs text-slate-400 font-mono">{accent}</span>
                  <span className="text-[10px] text-slate-500">Used for the countdown, charts and progress bars</span>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Client win quotes (a fresh one rotates in on each portal visit)</label>
                {quotes.map((q, i) => (
                  <div key={i} className="mb-2 rounded-md border border-white/10 bg-white/[0.02] p-2">
                    <textarea value={q.quote} onChange={(e) => setQuotes(qs => qs.map((x, j) => j === i ? { ...x, quote: e.target.value } : x))} rows={2} maxLength={300}
                      placeholder='"They doubled our small-dollar donations in six weeks."'
                      data-testid={`portal-testimonial-quote-${i}`}
                      className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-xs text-white placeholder-slate-600 focus:border-cyan-400/60 focus:outline-none resize-none" />
                    <div className="flex items-center gap-2 mt-1.5">
                      <input value={q.author} onChange={(e) => setQuotes(qs => qs.map((x, j) => j === i ? { ...x, author: e.target.value } : x))} maxLength={80}
                        placeholder="Attribution — e.g. Jane Doe, Campaign Manager" data-testid={`portal-testimonial-author-${i}`}
                        className="flex-1 bg-slate-900/80 border border-white/15 rounded-md px-3 py-1.5 text-xs text-white placeholder-slate-600 focus:border-cyan-400/60 focus:outline-none" />
                      <span className="text-[10px] text-slate-600 shrink-0">{q.quote.length}/300</span>
                      <button type="button" onClick={() => setQuotes(qs => qs.filter((_, j) => j !== i))}
                        data-testid={`portal-testimonial-remove-${i}`}
                        className="text-[10px] text-rose-300 hover:text-rose-200 shrink-0">Remove</button>
                    </div>
                  </div>
                ))}
                {quotes.length < 10 && (
                  <button type="button" onClick={() => setQuotes(qs => [...qs, { quote: "", author: "" }])}
                    data-testid="portal-testimonial-add"
                    className="text-xs text-cyan-300 hover:text-cyan-200 underline underline-offset-4">+ Add quote</button>
                )}
                <p className="text-[10px] text-slate-500 mt-1">Save with "Update branding". Remove all quotes to hide the section.</p>
                <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                  <div>
                    <label className="text-xs text-slate-200 font-medium">Show ad spend chart on portal</label>
                    <p className="text-[10px] text-slate-500">Proof of stakes — reported opponent ad buys, graphed over time.</p>
                  </div>
                  <button type="button" onClick={() => setShowSpend(s => !s)} data-testid="portal-show-spend-toggle"
                    className={`text-[10px] px-2.5 py-1 rounded-full border shrink-0 ${showSpend
                      ? "bg-emerald-400/10 border-emerald-400/30 text-emerald-300"
                      : "bg-white/5 border-white/15 text-slate-300 hover:bg-white/10"}`}>
                    {showSpend ? "Shown" : "Hidden"}
                  </button>
                </div>
                <div className="mt-3 pt-3 border-t border-white/10">
                  <label className="text-xs uppercase tracking-wider text-slate-400 font-medium block mb-1.5">Ask the client for a quote</label>
                  <div className="flex items-center gap-2">
                    <input value={quoteReqEmail} onChange={(e) => setQuoteReqEmail(e.target.value)} type="email"
                      placeholder="client@campaign.com" data-testid="quote-request-email"
                      className="flex-1 bg-slate-900/80 border border-white/15 rounded-md px-3 py-1.5 text-xs text-white placeholder-slate-600 focus:border-cyan-400/60 focus:outline-none" />
                    <button type="button" onClick={() => sendQuoteRequest(true)} disabled={quoteReqBusy}
                      data-testid="quote-request-send-btn"
                      className="text-xs px-3 py-1.5 rounded-md bg-cyan-400/15 text-cyan-200 border border-cyan-400/25 hover:bg-cyan-400/25 disabled:opacity-50 shrink-0">
                      {quoteReqBusy ? "…" : "Email link"}
                    </button>
                    <button type="button" onClick={() => sendQuoteRequest(false)} disabled={quoteReqBusy}
                      data-testid="quote-request-copy-btn"
                      className="text-xs px-3 py-1.5 rounded-md bg-white/5 text-slate-200 border border-white/15 hover:bg-white/10 disabled:opacity-50 shrink-0">
                      Copy link
                    </button>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1">One-tap page where the client writes a quote — it drops straight into the rotation above.</p>
                </div>
              </div>
            </div>
            {link && (
              <div className="rounded-xl border border-cyan-400/25 bg-cyan-400/[0.05] p-3 mb-4" data-testid="portal-link-row">
                <div className="text-[10px] uppercase tracking-widest text-cyan-300 mb-1">Live link</div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-300 truncate flex-1">{link}</span>
                  <button onClick={copy} data-testid="portal-copy-btn" className="text-cyan-300 hover:text-cyan-100 p-1"><Copy className="w-3.5 h-3.5" /></button>
                </div>
                {portal?.views != null && <div className="text-[10px] text-slate-500 mt-1">Opened {portal.views} time{portal.views === 1 ? "" : "s"}</div>}
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-cyan-400/15">
                  <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="candidate@campaign.com" data-testid="portal-invite-email"
                    className="flex-1 bg-slate-900/80 border border-white/15 rounded-md px-3 py-1.5 text-xs text-white placeholder-slate-600 focus:border-cyan-400/60 focus:outline-none" />
                  <button onClick={invite} disabled={inviting} data-testid="portal-invite-btn"
                    className="text-xs font-semibold px-3.5 py-1.5 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-900 hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-1.5 shrink-0">
                    {inviting ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Invite client
                  </button>
                </div>
                <div className="mt-2">
                  <textarea value={inviteMsg} onChange={(e) => setInviteMsg(e.target.value)} rows={2}
                    placeholder="Optional welcome note included in the invite email…" data-testid="portal-invite-message"
                    className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-2 text-xs text-white placeholder-slate-600 focus:border-cyan-400/60 focus:outline-none resize-none" />
                  <button onClick={saveTemplate} data-testid="portal-save-template-btn"
                    className="text-[10px] text-cyan-300 hover:text-cyan-100 mt-0.5">
                    Save as my reusable invite template
                  </button>
                </div>
                <div className="mt-3 pt-3 border-t border-cyan-400/15">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-[10px] uppercase tracking-widest text-cyan-300">Weekly highlights email to client</span>
                    <button onClick={() => setWeeklyOn((v) => !v)} data-testid="portal-weekly-toggle"
                      className={`w-9 h-5 rounded-full relative transition-colors shrink-0 ${weeklyOn ? "bg-cyan-400" : "bg-white/10"}`}>
                      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-paper transition-all ${weeklyOn ? "left-[18px]" : "left-0.5"}`} />
                    </button>
                  </div>
                  {weeklyOn && (
                    <input type="email" value={weeklyEmail} onChange={(e) => setWeeklyEmail(e.target.value)}
                      placeholder="Client email for Monday highlights" data-testid="portal-weekly-email"
                      className="w-full bg-slate-900/80 border border-white/15 rounded-md px-3 py-1.5 text-xs text-white placeholder-slate-600 focus:border-cyan-400/60 focus:outline-none" />
                  )}
                  <p className="text-[10px] text-slate-500 mt-1">Save with "Update branding" below.</p>
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Btn onClick={() => act("enable")} disabled={busy} data-testid="portal-enable-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                {portal?.enabled ? "Update branding" : "Enable portal"}
              </Btn>
              {portal?.enabled && (
                <>
                  <Btn variant="secondary" onClick={() => act("rotate")} disabled={busy} data-testid="portal-rotate-btn">
                    <RotateCcw className="w-4 h-4" /> Rotate link
                  </Btn>
                  <Btn variant="danger" onClick={() => act("disable")} disabled={busy} data-testid="portal-disable-btn">
                    <Ban className="w-4 h-4" /> Disable
                  </Btn>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
